"use client";

import React from "react";
import LoginForm from "@/components/auth/LoginForm";
import AuthIllustrationPanel from "@/components/auth/AuthIllustrationPanel";
import { getBackendRenderAttributes, isBackendRenderMode } from "@/lib/backend-render-utils";
import { useLanguageVariables } from "@/contexts/LanguageVariablesContext";
import Link from "next/link";
import { resolveUrlFromMap } from "@/lib/blog-transformers";
import type { PageUrlMap } from "@/types/blog";

interface LoginPageProps {
  language?: string;
  translations?: Record<string, string>;
  pageUrls?: PageUrlMap;
  defaultLanguage?: string;
  socialProviders?: Array<{ provider: string; isActive: boolean; name?: string }>;
  requireCaptcha?: boolean;
  formTitle?: string;
  __grapesPreview?: boolean;
}

export default function LoginPage({
  language = 'en',
  translations = {},
  pageUrls = {},
  defaultLanguage = 'en',
  socialProviders = [],
  requireCaptcha = true,
  formTitle,
  __grapesPreview = false,
  ...restProps
}: LoginPageProps) {
  const isBackendRender = isBackendRenderMode({ __grapesPreview, ...restProps });
  const backendAttributes = getBackendRenderAttributes(
    'page_layouts/LoginPage',
    { language, translations, pageUrls, socialProviders },
    isBackendRender
  );
  const { getVariable } = useLanguageVariables();

  const t = (key: string, fallback: string) => {
    return translations?.[key] || getVariable(key, fallback);
  };

  const registerUrl = resolveUrlFromMap(pageUrls, 'register', language, defaultLanguage, '#');

  return (
    <div {...backendAttributes} className="min-h-screen bg-gray-50">
      <div className="flex min-h-screen">
        <AuthIllustrationPanel variant="login" />

        <div className="flex-1 flex items-center justify-center p-8 lg:p-12">
          <div className="w-full max-w-md">
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                {t("auth.login.title", formTitle || "Welcome Back")}
              </h1>
              <p className="text-gray-600">
                {t("auth.login.subtitle", "Sign in to your account to continue")}
              </p>
            </div>

            <div className="bg-white rounded-lg shadow-lg p-8">
              <LoginForm
                language={language}
                translations={translations}
                pageUrls={pageUrls}
                socialProviders={socialProviders}
                defaultLanguage={defaultLanguage}
                requireCaptcha={requireCaptcha}
              />
            </div>

            {registerUrl !== '#' && (
              <div className="mt-6 text-center">
                <p className="text-sm text-gray-600">
                  {t("auth.login.no_account", "Don't have an account?")}{" "}
                  <Link
                    href={registerUrl}
                    className="font-medium text-red-600 hover:text-red-700 transition-colors"
                  >
                    {t("auth.login.register_link", "Register")}
                  </Link>
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
